import { Link } from "react-router-dom";
import { Sparkles, Heart, BookOpen, Users, Shield, Cross } from "lucide-react";

export default function About() {
    return (
        <div data-testid="about-page">
            <section className="border-b-2 border-ink bg-lilac/40">
                <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
                    <span className="pill bg-white">Our story</span>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3">
                        Helping kids to walk with Jesus — one day at a time
                    </h1>
                    <p className="mt-5 text-lg text-ink/80 max-w-2xl mx-auto">
                        2nd Mile Kids is a free, daily Christian Bible blog made for families. Every lesson is rooted in the Holy Bible
                        (ESV) and pairs Scripture with simple, Christ-like ways to love your neighbor.
                    </p>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
                <div className="card-sticker p-8 bg-white">
                    <p className="font-accent text-3xl text-coral">why "2nd Mile"?</p>
                    <p className="mt-3 text-lg leading-relaxed">
                        Jesus said, <span className="italic">"If anyone forces you to go one mile, go with him two miles."</span> — Matthew 5:41 (ESV).
                        That's the heart of this blog. Following Jesus isn't about doing the bare minimum. It's about going further than expected
                        in love, kindness, and service. Even the youngest disciples can shine real light into the world for Christ.
                    </p>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-10 grid md:grid-cols-2 gap-6">
                {[
                    { icon: BookOpen, color: "bg-sun", title: "Scripture-first (ESV only)", desc: "Every lesson is rooted in the Holy Bible. All Scripture quotations are from the English Standard Version." },
                    { icon: Cross, color: "bg-coral", title: "Christ-centered", desc: "Lessons point kids to Jesus Christ — His life, death, resurrection, and call to love God and neighbor." },
                    { icon: Heart, color: "bg-mint", title: "Age-by-age", desc: "Two explanations per lesson — one for ages 5–8, one for ages 9–12 — both faithful to the Gospel." },
                    { icon: Users, color: "bg-sky", title: "Action-ready", desc: "Three Christ-like service ideas accompany every lesson, from easy to big-impact, applying the lesson in your community." },
                ].map((it, i) => (
                    <div key={i} className="card-sticker p-6 bg-white" data-testid={`about-feature-${i}`}>
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl border-2 border-ink ${it.color} mb-3`}>
                            <it.icon className="w-6 h-6 text-ink" strokeWidth={2.5} />
                        </div>
                        <h3 className="font-heading text-xl font-bold">{it.title}</h3>
                        <p className="text-ink/70 mt-1 leading-relaxed">{it.desc}</p>
                    </div>
                ))}
            </section>

            <section className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
                <div className="card-sticker p-7 bg-cream" data-testid="about-statement-of-faith">
                    <p className="font-accent text-3xl text-coral">what we believe</p>
                    <ul className="mt-3 space-y-2 text-ink/85 leading-relaxed list-disc pl-6">
                        <li>The Holy Bible (Old and New Testaments) is the inspired Word of God.</li>
                        <li>There is one God, eternally existing in three persons: Father, Son, and Holy Spirit.</li>
                        <li>Jesus Christ is fully God and fully man, who died and rose again for our salvation.</li>
                        <li>Salvation is by grace through faith in Jesus Christ alone (Ephesians 2:8–9, ESV).</li>
                        <li>Believers are called to love God, love neighbor, and make disciples (Matthew 22:37–39; 28:19).</li>
                    </ul>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
                <Sparkles className="w-12 h-12 mx-auto text-coral" strokeWidth={2.5} />
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-4">
                    Ready to start today?
                </h2>
                <p className="text-ink/70 mt-2">A fresh story from God's Word is waiting.</p>
                <Link to="/" className="btn-sticker bg-sun text-ink mt-6 inline-flex" data-testid="about-cta">
                    Read today's lesson
                </Link>
            </section>
        </div>
    );
}
